export const STEP_KEYS = [
  'basic',
  'visa',
  'conditions',
  'requirements',
  'details',
  'deadline',
  'perks',
];

export const STEP_TITLES = {
  basic: ['Basic Job Info', '기본 정보'],
  visa: ['Visa & Eligibility', '비자 및 지원 자격'],
  conditions: ['Conditions', '근무 조건'],
  requirements: ['Requirements', '지원 자격'],
  details: ['Details', '상세 내용'],
  deadline: ['Application deadline', '지원 마감'],
  perks: ['Perks and Benefits', '복리후생'],
};

export const STEP_FIELDS = {
  basic: [
    'company',
    'workLocation',
    'companyAddress',
    'jobTitle',
    'role',
    'customRole',
    'employmentType',
  ],
  visa: ['visaTypes'],
  conditions: ['workingSchedule'],
  requirements: ['koreanSkills', 'nationality', 'requiredLanguage'],
  details: ['aboutRole', 'jobDescription'],
  deadline: [],
  perks: [],
};

export const JOB_TITLE_MAX_LENGTH = 200;

const hasText = (value) => Boolean(String(value ?? '').trim());
const hasAny = (list) => Boolean((list || []).filter(Boolean).length);

const isCustomRole = (role) =>
  ['Other', 'Other (Enter manually)'].includes(role);

/**
 * 단계별 검증 규칙.
 */
const RULES = {
  basic: (values, t) => {
    const errors = {};
    if (!hasText(values.company)) {
      errors.company = t('Enter a company name.', '회사명을 입력해 주세요.');
    }
    if (!hasText(values.workLocation)) {
      errors.workLocation = t(
        'Select a work location.',
        '근무 지역을 선택해 주세요.',
      );
    }
    if (!hasText(values.companyAddress)) {
      errors.companyAddress = t(
        'Enter a company address.',
        '회사 주소를 입력해 주세요.',
      );
    }
    if (!hasText(values.jobTitle)) {
      errors.jobTitle = t('Enter a job title.', '공고 제목을 입력해 주세요.');
    } else if (String(values.jobTitle).trim().length > JOB_TITLE_MAX_LENGTH) {
      // 서버 컬럼 길이와 같은 값 — 넘기면 저장 시 500 이 난다
      errors.jobTitle = t(
        `Job title must be ${JOB_TITLE_MAX_LENGTH} characters or fewer.`,
        `공고 제목은 ${JOB_TITLE_MAX_LENGTH}자 이하로 입력해 주세요.`,
      );
    }
    if (!hasText(values.role)) {
      errors.role = t('Select a role.', '직무를 선택해 주세요.');
    }
    if (isCustomRole(values.role) && !hasText(values.customRole)) {
      errors.customRole = t('Enter a role title.', '직무명을 입력해 주세요.');
    }
    if (!hasText(values.employmentType)) {
      errors.employmentType = t(
        'Select an employment type.',
        '고용 형태를 선택해 주세요.',
      );
    }
    return errors;
  },

  visa: (values, t) => {
    const errors = {};
    if (!(values.visaTypes || []).length) {
      errors.visaTypes = t(
        'Select at least one preferred visa type.',
        '선호 비자 유형을 1개 이상 선택해 주세요.',
      );
    }
    return errors;
  },

  conditions: (values, t) => {
    const errors = {};
    if (!hasText(values.workingSchedule)) {
      errors.workingSchedule = t(
        'Select a working schedule.',
        '근무 형태를 선택해 주세요.',
      );
    }
    return errors;
  },

  requirements: (values, t) => {
    const errors = {};
    if (!hasText(values.koreanSkills)) {
      errors.koreanSkills = t(
        'Select a Korean skills level.',
        '한국어 능력 수준을 선택해 주세요.',
      );
    }
    if (
      !hasAny(values.applicantNationality) &&
      !values.noNationalitySelection
    ) {
      errors.nationality = t(
        'Select a nationality or No selection.',
        '국적을 선택하거나 선택 안 함을 체크해 주세요.',
      );
    }
    // careerLevel 은 선택 항목이다. 비워두면 '경력 무관'으로 본다
    if (
      !hasAny(values.requiredLanguage) &&
      !values.noRequiredLanguageSelection
    ) {
      errors.requiredLanguage = t(
        'Select a required language or No selection.',
        '필요 언어를 선택하거나 선택 안 함을 체크해 주세요.',
      );
    }
    return errors;
  },

  details: (values, t) => {
    const errors = {};
    if (!hasText(values.aboutRole)) {
      errors.aboutRole = t(
        'Enter details about the role.',
        '직무 소개를 입력해 주세요.',
      );
    }
    if (!hasText(values.jobDescription)) {
      errors.jobDescription = t(
        'Enter a job description.',
        '상세 업무 내용을 입력해 주세요.',
      );
    }
    return errors;
  },

  deadline: () => ({}),
  perks: () => ({}),
};

export const validateStep = (stepKey, textValues, t) =>
  (RULES[stepKey] ?? (() => ({})))(textValues, t);

export const validateAll = (textValues, t) =>
  STEP_KEYS.reduce(
    (acc, stepKey) => ({ ...acc, ...validateStep(stepKey, textValues, t) }),
    {},
  );

export const findStepOfField = (field) =>
  STEP_KEYS.find((stepKey) => STEP_FIELDS[stepKey].includes(field)) ??
  STEP_KEYS[0];
